"use client";

import { useState, useEffect, useCallback } from "react";
import { SidebarTrigger } from "@/shadcnComponents/ui/sidebar";
import { Button } from "@/shadcnComponents/ui/button";
import { Input } from "@/shadcnComponents/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcnComponents/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shadcnComponents/ui/table";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown } from "lucide-react";
import { Badge } from "@/shadcnComponents/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shadcnComponents/ui/dialog";
import { Label } from "@/shadcnComponents/ui/label";
import { Textarea } from "@/shadcnComponents/ui/textarea";
import { useParams } from "react-router-dom";
import { useToast } from "@/shadcnComponents/ui/use-toast";
const host: string = import.meta.env.VITE_BACKEND_SERVER_URL;

type Influencer = {
  id: number;
  photo: string;
  name: string;
  email: string;
  phoneNumber: string;
  campaignName: string;
  approxEarning: number;
  campaignType: "performance" | "fixed";
  status: "Confirmed" | "Decline Requested" | "Rejected";
  declineReason: string | null;
  declineRequestTime: string | null;
  declineComment: string | null;
  adminStatus: "accepted" | "pending" | "rejected" | null;
  adminComment: string | null;
  adminUpdatedAt: string | null;
  earningShownToInfluencer: number;
};

export default function AdminCampaignReviewPage() {
  const params = useParams();
  const campaignId = params.id;
  const { toast } = useToast();
  const [influencers, setInfluencers] = useState<Influencer[]>([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [adminStatusFilter, setAdminStatusFilter] = useState<string | null>(
    null
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filteredInfluencers, setFilteredInfluencers] = useState(influencers);
  const [selectedInfluencer, setSelectedInfluencer] =
    useState<Influencer | null>(null);
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);
  const [newAdminStatus, setNewAdminStatus] =
    useState<Influencer["adminStatus"]>(null);
  const [newAdminComment, setNewAdminComment] = useState("");

  const columns: ColumnDef<Influencer>[] = [
    {
      accessorKey: "photo",
      header: "Photo",
      cell: ({ row }) => (
        <img
          src={row.getValue("photo") || "/placeholder.svg"}
          alt={`${row.getValue("name")}'s photo`}
          width={40}
          height={40}
          className="rounded-full"
        />
      ),
    },
    {
      accessorKey: "name",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
    },
    {
      accessorKey: "email",
      header: "Email ID",
    },
    {
      accessorKey: "phoneNumber",
      header: "Phone Number",
    },
    {
      accessorKey: "approxEarning",
      header: "Approx Earning",
      cell: ({ row }) => {
        const amount = Number.parseFloat(row.getValue("approxEarning"));
        const formatted = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "INR",
        }).format(amount);
        return (
          <Badge variant="secondary" className="font-medium">
            {formatted}
          </Badge>
        );
      },
    },
    {
      accessorKey: "campaignType",
      header: "Campaign Type",
      cell: ({ row }) => {
        const campaignType = row.getValue("campaignType") as string;
        return (
          <Badge
            variant="outline"
            className={
              campaignType === "performance" ? "bg-blue-100" : "bg-green-100"
            }
          >
            {campaignType}
          </Badge>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("status") as string;
        return (
          <Badge
            variant="outline"
            className={
              status === "Confirmed" ? "bg-green-100" : "bg-yellow-100"
            }
          >
            {status}
          </Badge>
        );
      },
    },
    {
      accessorKey: "declineReason",
      header: "Decline Reason",
    },
    {
      accessorKey: "adminStatus",
      header: "Admin Status",
      cell: ({ row }) => {
        const adminStatus = row.getValue("adminStatus") as string;
        let badgeClass = "bg-gray-100";
        if (adminStatus === "Request Approved") badgeClass = "bg-blue-100";
        if (adminStatus === "Pending Collab") badgeClass = "bg-yellow-100";
        if (adminStatus === "Confirmed Collab") badgeClass = "bg-green-100";
        return adminStatus ? (
          <Badge variant="outline" className={badgeClass}>
            {adminStatus}
          </Badge>
        ) : (
          "N/A"
        );
      },
    },
    {
      accessorKey: "adminComment",
      header: "Admin Comment",
      cell: ({ row }) => {
        return (
          <div className="flex text-wrap text-left min-w-[300px] ">
            {row.getValue("adminComment") || "N/A"}
          </div>
        );
      },
    },
    {
      accessorKey: "earningShownToInfluencer",
      header: "Earning Shown to Influencer",
      cell: ({ row }) => {
        const amount = Number.parseFloat(
          row.getValue("earningShownToInfluencer")
        );
        const formatted = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "INR",
        }).format(amount);
        return (
          <Badge variant="secondary" className="font-medium">
            {formatted}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleAction(row.original)}
            disabled={
              row.original.status !== "Decline Requested" &&
              row.original.status !== "Confirmed"
            }
          >
            Take Action
          </Button>
        );
      },
    },
  ];

  const table = useReactTable({
    data: filteredInfluencers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  const handleAction = (influencer: Influencer) => {
    setSelectedInfluencer(influencer);
    setIsActionDialogOpen(true);
    if (influencer.status === "Confirmed") {
      setNewAdminStatus(influencer.adminStatus);
      setNewAdminComment(influencer.adminComment || "");
    } else {
      setNewAdminStatus(null);
      setNewAdminComment("");
    }
  };

  const handleSubmitAction = async () => {
    if (!newAdminStatus || !newAdminComment) {
      toast({
        title: "Please fill all the fields",
        description: "Admin status and comment are mandatory",
        variant: "destructive",
      });
      return;
    }

    if (selectedInfluencer && newAdminStatus && newAdminComment) {
      try {
        const response = await fetch(
          `${host}/developer/brand/submitCollabReview`,
          {
            method: "POST",
            body: JSON.stringify({
              influencerStatus:
                newAdminStatus === "rejected"
                  ? "accepted"
                  : newAdminStatus === "pending"
                  ? "pending"
                  : "rejected",
              influencerId: selectedInfluencer.id,
              adminStatus: newAdminStatus,
              adminComment: newAdminComment,
              collabId: campaignId,
            }),
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              "Access-Control-Allow-Credentials": "true",
              jwtToken: localStorage.getItem("jwtTokenD"),
            },
          }
        );
        const json = await response.json();
        if (json.success) {
          getInfluencers();
          toast({
            title: "Action submitted successfully",
            variant: "default",
          });
        } else {
          toast({
            title: "Failed to submit action",
            description: json.message,
            variant: "destructive",
          });
        }
      } catch (error) {
        toast({
          title: "Failed to submit action",
          description: "Please try again later",
          variant: "destructive",
        });
      }

      setIsActionDialogOpen(false);
      setNewAdminStatus(null);
      setNewAdminComment("");
    }
  };

  const getInfluencers = useCallback(async () => {
    try {
      const response = await fetch(
        `${host}/developer/brand/collabReview/${campaignId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "Access-Control-Allow-Credentials": "true",
            jwtToken: localStorage.getItem("jwtTokenD"),
          },
        }
      );
      const json = await response.json();
      if (json.success) {
        setInfluencers(json?.data);
      }
    } catch (error) {
      console.error(error);
    }
  }, [campaignId]);

  useEffect(() => {
    const filtered = influencers.filter((influencer) => {
      const matchesSearch =
        influencer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        influencer.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter
        ? influencer.status === statusFilter || statusFilter === "all"
        : true;
      const matchesAdminStatus = adminStatusFilter
        ? influencer.adminStatus === adminStatusFilter ||
          adminStatusFilter === "all"
        : true;
      return matchesSearch && matchesStatus && matchesAdminStatus;
    });
    setFilteredInfluencers(filtered);
  }, [searchTerm, statusFilter, adminStatusFilter, influencers]);
  useEffect(() => {
    getInfluencers();
  }, [getInfluencers]);

  return (
    <>
      <div className="w-full">
        <div className="flex flex-col h-screen">
          <header className="flex h-16 shrink-0 items-center gap-2 border-b px-6">
            <SidebarTrigger />
            <h1 className="text-2xl font-bold">
              {influencers?.length > 0
                ? influencers?.[0]?.campaignName
                : "Campaign Not Found"}
            </h1>
          </header>
          <main className="flex-1 overflow-auto p-6">
            <div className="mb-6 flex flex-wrap items-center gap-4">
              <Input
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-xs"
              />
              <Select onValueChange={(value) => setStatusFilter(value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Confirmed">Confirmed</SelectItem>
                  <SelectItem value="Decline Requested">
                    Decline Requested
                  </SelectItem>
                </SelectContent>
              </Select>
              <Select onValueChange={(value) => setAdminStatusFilter(value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by Admin Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Admin Statuses</SelectItem>
                  <SelectItem value="accepted">Request Approved</SelectItem>
                  <SelectItem value="pending">Pending Collab</SelectItem>
                  <SelectItem value="rejected">Confirmed Collab</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-2">
                {statusFilter && statusFilter !== "all" && (
                  <Badge variant="secondary" className="px-2 py-1">
                    Status: {statusFilter}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 h-auto p-0 text-xs"
                      onClick={() => setStatusFilter(null)}
                    >
                      ×
                    </Button>
                  </Badge>
                )}
                {adminStatusFilter && adminStatusFilter !== "all" && (
                  <Badge variant="secondary" className="px-2 py-1">
                    Admin Status: {adminStatusFilter}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 h-auto p-0 text-xs"
                      onClick={() => setAdminStatusFilter(null)}
                    >
                      ×
                    </Button>
                  </Badge>
                )}
              </div>
            </div>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        No results.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </main>
        </div>
        <Dialog open={isActionDialogOpen} onOpenChange={setIsActionDialogOpen}>
          <DialogContent className="sm:max-w-[425px] max-h-[600px] overflow-auto">
            <DialogHeader>
              <DialogTitle>Take Action</DialogTitle>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">Overview</h3>
                <div className="space-y-2">
                  <p>
                    <strong>Influencer:</strong> {selectedInfluencer?.name}
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    <Badge
                      variant="outline"
                      className={
                        selectedInfluencer?.status === "Confirmed"
                          ? "bg-green-100"
                          : "bg-yellow-100"
                      }
                    >
                      {selectedInfluencer?.status}
                    </Badge>
                  </p>
                  {selectedInfluencer?.status === "Decline Requested" && (
                    <>
                      <p>
                        <strong>Declined at:</strong>{" "}
                        {new Date(
                          selectedInfluencer.declineRequestTime!
                        ).toLocaleString()}
                      </p>
                      <p>
                        <strong>Influencer Reason:</strong>{" "}
                        {selectedInfluencer.declineReason}
                      </p>
                      <p>
                        <strong>Influencer Decline Comment:</strong>{" "}
                        {selectedInfluencer.declineComment || "N/A"}
                      </p>
                    </>
                  )}
                  <p>
                    <strong>Admin Status:</strong>{" "}
                    {selectedInfluencer?.adminStatus ? (
                      <Badge
                        variant="outline"
                        className={
                          selectedInfluencer.adminStatus === "accepted"
                            ? "bg-blue-100"
                            : selectedInfluencer.adminStatus === "pending"
                            ? "bg-yellow-100"
                            : selectedInfluencer.adminStatus === "rejected"
                            ? "bg-green-100"
                            : "bg-gray-100"
                        }
                      >
                        {selectedInfluencer.adminStatus}
                      </Badge>
                    ) : (
                      "N/A"
                    )}
                  </p>
                  <p>
                    <strong>Admin Comment:</strong>{" "}
                    {selectedInfluencer?.adminComment || "N/A"}
                  </p>
                  <p>
                    <strong>Last Updated:</strong>{" "}
                    {selectedInfluencer?.adminUpdatedAt
                      ? new Date(
                          selectedInfluencer.adminUpdatedAt
                        ).toLocaleString()
                      : "N/A"}
                  </p>
                </div>
              </div>

              {selectedInfluencer?.status === "Decline Requested" && (
                <div className="space-y-2">
                  <h3 className="font-semibold">Take action</h3>
                  <div className="space-y-1">
                    <Label htmlFor="status">Change status</Label>
                    <Select
                      onValueChange={(value) =>
                        setNewAdminStatus(value as Influencer["adminStatus"])
                      }
                    >
                      <SelectTrigger id="status">
                        <SelectValue placeholder="Select new status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="accepted">
                          Request Approved
                        </SelectItem>
                        <SelectItem value="pending">Pending Collab</SelectItem>
                        <SelectItem value="rejected">
                          Confirmed Collab
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="comment">Comments (mandatory)</Label>
                    <Textarea
                      id="comment"
                      placeholder="Enter your comment"
                      value={newAdminComment}
                      onChange={(e) => setNewAdminComment(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
            {selectedInfluencer?.status === "Decline Requested" && (
              <Button
                onClick={handleSubmitAction}
                disabled={!newAdminStatus || !newAdminComment}
              >
                Submit
              </Button>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
